import gsap, { Power1 } from 'gsap';
import * as Header from '../components/header';

/**
 * @param {*} nodeElement - every possible html element
 * @param {*} substractOrAdditionToScrollOffset - pixels that should be added or substracted from final offsetTop
 * @param {*} scrollingTime - default 900 ms
 */
export function scrollToTarget(nodeElement, substractOrAdditionToScrollOffset = 0, scrollingTime = 900) {
    if (!nodeElement) return;

    const targetOffset = calculateOffsetTop(nodeElement);
    animateScrolling(targetOffset + substractOrAdditionToScrollOffset, scrollingTime);
}

export function scrollToAnchor(e, link) {
    const exludedLinks = ['#easy-popup-', '#skipNavigation', '#ccSkipContent'];

    if (!link.hash || link.dataset?.fslightbox || exludedLinks.some((value) => link.hash.startsWith(value))) {
        return;
    }

    const target = document.querySelector(link.hash);

    if (!location.pathname.includes(link.pathname) || !target) return;

    if (e) e.preventDefault();
    scrollToTarget(target);
}

export function isInViewport(element) {
    const rect = element.getBoundingClientRect();

    return (
        rect.top >= Header.getInnerHeight() &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth
    );
}

function calculateOffsetTop(target) {
    const targetStyles = getComputedStyle(target);

    let marginTop = Number.parseInt(targetStyles.marginTop) / 2;
    marginTop = marginTop < 0 ? 0 : marginTop;

    const endScrollOffset = target.getBoundingClientRect().top + document.documentElement.scrollTop;

    const headerHeight = endScrollOffset > Header.getFixedPosition() ? Header.getInnerHeight() : 0;

    /* Unter dem Header können weitere fixierte Leisten kleben (z.B. eine Abschnitts-Subnav).
       Deren Gesamthöhe steht per Konvention in `scroll-padding-top` am <html>: der Browser
       zieht sie beim direkten Hash-Sprung ab, dieses JS muss dasselbe tun — sonst landet
       das Anker-Ziel hinter der Leiste (gemessen: Sektion bei y119 statt y194, Überschrift
       25px verdeckt). Ohne `scroll-padding-top` (Standard `auto`) bleibt alles wie bisher. */
    const scrollPaddingTop = Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;

    return endScrollOffset - Math.max(headerHeight, scrollPaddingTop) - marginTop + 1;
}

function animateScrolling(targetOffset, scrollingTime) {
    /* Ziel auf den erreichbaren Bereich klemmen: liegt es hinter dem Seitenende, bremst der
       Browser den Tween hart am Maximum ab statt weich auszulaufen (kurze Seiten mit Ankern
       nahe dem Ende, z. B. die Vereinsarbeit-Übersicht). */
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const clampedOffset = Math.max(0, Math.min(targetOffset, maxScroll));

    /* Sticky-Header während des Anfahrens festhalten: header.js setzt bei .auto-scroll
       im Sticky-Zustand .keep, statt den Header zwischen Headerhöhe und halber Viewport-
       höhe aus- und ab der Mitte wieder einzufahren — bei kurzen Distanzen passiert
       beides innerhalb einer Animation und ruckt sichtbar (mit der angehefteten Subnav
       als Gruppe). Nach der Ankunft: liegt das Ziel im „show"-Bereich, ist .keep
       überflüssig; davor bleibt es bis zur nächsten Nutzer-Eingabe, damit der Header
       nicht direkt nach der Landung noch wegklappt. */
    // laufenden Tween zuerst beenden (dessen onInterrupt räumt die Klassen), dann neu setzen
    gsap.killTweensOf('html, body');
    const header = document.querySelector('#header');
    if (header) header.classList.add('auto-scroll');

    const releaseHeader = () => {
        if (!header) return;
        header.classList.remove('auto-scroll');
        if (document.documentElement.scrollTop > Header.getFixedPosition()) {
            header.classList.remove('keep');
            return;
        }
        const release = () => {
            header.classList.remove('keep');
            ['wheel', 'touchstart', 'keydown'].forEach((type) => window.removeEventListener(type, release));
        };
        ['wheel', 'touchstart', 'keydown'].forEach((type) => window.addEventListener(type, release, { passive: true }));
    };

    gsap.to('html, body', {
        scrollTop: clampedOffset,
        duration: scrollingTime / 1000,
        overwrite: true,
        ease: Power1.easeInOut,
        onComplete: releaseHeader,
        onInterrupt: releaseHeader,
    });
}
